/**
 * Adresse de facturation : ce que chaque pays demande vraiment.
 *
 * Le champ code postal suit l'usage postal du pays choisi :
 * - obligatoire là où la poste l'exige (France, Belgique, Suisse, États-Unis,
 *   Canada...) : tout le monde y connaît le sien ;
 * - facultatif là où il existe sans servir au quotidien (Sénégal, Maroc,
 *   Algérie...) ;
 * - masqué là où il n'existe pas (Côte d'Ivoire, Cameroun, Bénin...), où il
 *   obligeait à inventer un numéro pour pouvoir payer.
 * Aux États-Unis et au Canada, le format est en plus vérifié.
 *
 * Source des trois listes et des exemples : données d'adresse de Google
 * (libaddressinput, chromium-i18n.appspot.com/ssl-address/data, champs fmt,
 * require et zipex), relevées le 16/09/2026 pour les 236 pays proposés.
 *
 * Les serveurs n'exigent que le minimum de Stripe Tax, le code postal aux
 * États-Unis et au Canada (isPostalCodeRequiredByStripe), règle doublée côté
 * back (UserController : salesSaveUser, salesCreateUser, updateFacturation).
 */

// Les 236 pays proposés dans les formulaires (libellés : clés
// address_selectedCountry_XX). L'ordre affiché suit la langue du visiteur,
// voir hooks/useCountryOptions.js.
// prettier-ignore
export const BILLING_COUNTRY_CODES = [
    "AC", "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AT",
    "AU", "AW", "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI",
    "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY",
    "BZ", "CA", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO",
    "CR", "CV", "CW", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC",
    "EE", "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FO", "FR", "GA",
    "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ",
    "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HN", "HR", "HT", "HU", "ID",
    "IE", "IL", "IM", "IN", "IO", "IQ", "IS", "IT", "JE", "JM", "JO", "JP",
    "KE", "KG", "KH", "KI", "KM", "KN", "KR", "KW", "KY", "KZ", "LA", "LB",
    "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD",
    "ME", "MF", "MG", "MK", "ML", "MM", "MN", "MO", "MQ", "MR", "MS", "MT",
    "MU", "MV", "MW", "MX", "MY", "MZ", "NA", "NC", "NE", "NG", "NI", "NL",
    "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK",
    "PL", "PM", "PN", "PR", "PS", "PT", "PY", "QA", "RE", "RO", "RS", "RU",
    "RW", "SA", "SB", "SC", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM",
    "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SZ", "TA", "TC", "TD", "TF",
    "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW",
    "TZ", "UA", "UG", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VN", "VU",
    "WF", "WS", "XK", "YE", "YT", "ZA", "ZM", "ZW",
];

// Seuls pays où Stripe Tax exige le code postal : il y situe l'État ou la
// province. Partout ailleurs, le pays lui suffit.
const STRIPE_POSTAL_CODE_COUNTRIES = ["US", "CA"];

// Pays où la poste exige le code postal : le champ y est obligatoire
// prettier-ignore
const POSTAL_CODE_REQUIRED_COUNTRIES = new Set([
    "AT", "AU", "AX", "BE", "BL", "BR", "CA", "CH", "CN", "CZ", "DE", "DK",
    "EE", "ES", "FI", "FK", "FR", "GB", "GF", "GG", "GL", "GP", "GR", "GS",
    "GU", "HU", "IM", "IN", "IO", "IT", "JE", "JP", "KR", "LI", "LT", "LU",
    "LV", "MF", "MQ", "MX", "MY", "NC", "NL", "NO", "NZ", "PF", "PL", "PM",
    "PN", "PR", "PT", "RE", "RO", "RU", "SE", "SG", "SH", "SJ", "SK", "SM",
    "TC", "TR", "TW", "UA", "US", "WF", "YT", "ZA",
]);

// Pays qui n'utilisent pas de code postal : le champ n'y est pas affiché
// prettier-ignore
const COUNTRIES_WITHOUT_POSTAL_CODE = new Set([
    "AE", "AG", "AO", "AQ", "AW", "BF", "BI", "BJ", "BO", "BQ", "BS", "BV",
    "BW", "BZ", "CD", "CF", "CG", "CI", "CK", "CM", "CW", "DJ", "DM", "ER",
    "FJ", "GA", "GD", "GH", "GM", "GQ", "GY", "HK", "JM", "KI", "KM", "KN",
    "LC", "LY", "ML", "MO", "MR", "MS", "MW", "NR", "NU", "PA", "PS", "QA",
    "RW", "SB", "SC", "SL", "SR", "SS", "ST", "SX", "TD", "TF", "TG", "TK",
    "TL", "TO", "TV", "UG", "VU", "WS", "YE", "ZW",
]);

// Un vrai code du pays, affiché en exemple dans le champ (villes connues pour
// la France, la Belgique, la Suisse, l'Algérie, le Maroc et le Sénégal)
// prettier-ignore
const POSTAL_CODE_EXAMPLES = {
    AC: "ASCN 1ZZ", AD: "AD100", AF: "1001", AI: "2640", AL: "1001", AM: "375010",
    AR: "C1070AAM", AT: "1010", AU: "2060", AX: "22150", AZ: "1000", BA: "71000",
    BB: "BB23026", BD: "1340", BE: "1000", BG: "1000", BH: "317", BL: "97100",
    BM: "FL 07", BN: "BT2328", BR: "40301-110", BT: "11001", BY: "223016", CA: "H3Z 2Y7",
    CH: "1201", CL: "8340457", CN: "266033", CO: "111221", CR: "1000", CV: "7600",
    CY: "2008", CZ: "100 00", DE: "26133", DK: "8660", DO: "11903", DZ: "16027",
    EC: "090105", EE: "69501", EG: "4460232", EH: "70000", ES: "28039", ET: "1000",
    FI: "00550", FK: "FIQQ 1ZZ", FO: "100", FR: "75011", GB: "EC1Y 8SY", GE: "0101",
    GF: "97300", GG: "GY1 1AA", GI: "GX11 1AA", GL: "3900", GN: "001", GP: "97100",
    GR: "151 24", GS: "SIQQ 1ZZ", GT: "09001", GU: "96910", GW: "1000", HN: "31301",
    HR: "10000", HT: "6120", HU: "1037", ID: "40115", IE: "A65 F4E2", IL: "9614303",
    IM: "IM2 1AA", IN: "110034", IO: "BBND 1ZZ", IQ: "31001", IS: "320", IT: "00144",
    JE: "JE1 1AA", JO: "11937", JP: "154-0023", KE: "20100", KG: "720001", KH: "120101",
    KR: "03051", KW: "54541", KY: "KY1-1100", KZ: "040900", LA: "01160", LB: "2038 3054",
    LI: "9496", LK: "20000", LR: "1000", LS: "100", LT: "04340", LU: "4750",
    LV: "LV-1073", MA: "20050", MC: "98000", MD: "2012", ME: "81257", MF: "97100",
    MG: "501", MK: "1314", MM: "11181", MN: "65030", MQ: "97220", MT: "NXR 01",
    MU: "42602", MV: "20026", MX: "02860", MY: "43000", MZ: "1102", NA: "10001",
    NC: "98814", NE: "8001", NG: "930283", NI: "52000", NL: "1234 AB", NO: "0025",
    NP: "44601", NZ: "6001", OM: "133", PE: "LIMA 23", PF: "98709", PG: "111",
    PH: "1008", PK: "44000", PL: "00-950", PM: "97500", PN: "PCRN 1ZZ", PR: "00930",
    PT: "2725-079", PY: "001001", RE: "97400", RO: "060274", RS: "106314", RU: "247112",
    SA: "11564", SE: "11455", SG: "546080", SH: "STHL 1ZZ", SI: "4000", SJ: "9170",
    SK: "010 01", SM: "47890", SN: "10000", SO: "JH 09010", SV: "1101", SZ: "H100",
    TA: "TDCU 1ZZ", TC: "TKCA 1ZZ", TH: "10150", TJ: "735450", TM: "744000", TN: "1002",
    TR: "01960", TT: "500234", TW: "104", TZ: "6090", UA: "15432", US: "10001",
    UY: "11600", UZ: "702100", VA: "00120", VC: "VC0100", VE: "1010", VG: "VG1110",
    VN: "70010", WF: "98600", XK: "10000", YT: "97600", ZA: "0083", ZM: "50100",
};

const US_ZIP_CODE = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_CODE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/;

/** Le pays utilise-t-il des codes postaux ? (pays inconnu : oui, par prudence) */
export function hasPostalCode(country) {
    return !COUNTRIES_WITHOUT_POSTAL_CODE.has(country);
}

/** La poste du pays exige-t-elle le code postal ? Règle des formulaires. */
export function isPostalCodeRequired(country) {
    return POSTAL_CODE_REQUIRED_COUNTRIES.has(country);
}

/**
 * Stripe Tax a-t-il besoin du code postal pour ce pays ? Seul contrôle des
 * routes serveur : un formulaire plus exigeant ne doit jamais faire refuser
 * côté serveur un paiement que Stripe accepterait.
 */
export function isPostalCodeRequiredByStripe(country) {
    return STRIPE_POSTAL_CODE_COUNTRIES.includes(country);
}

/**
 * Tout ce qu'il faut pour afficher le champ code postal d'un pays.
 * @param {string} country code ISO du pays choisi
 */
export function getPostalCodeField(country) {
    const example = POSTAL_CODE_EXAMPLES[country] || "";
    const required = isPostalCodeRequired(country);
    let labelKey = required ? "address_zipCode" : "address_zipCode_optional";
    if (country === "US") labelKey = "address_zipCode_us";
    return {
        visible: hasPostalCode(country),
        required,
        labelKey,
        example,
        // Clavier numérique sur mobile quand le code ne compte que des chiffres
        inputMode: /^\d+$/.test(example) ? "numeric" : "text",
        autoCapitalize: /[A-Z]/.test(example) ? "characters" : "off",
    };
}

/**
 * Remet un code postal saisi à la forme attendue : majuscules, espaces
 * superflus retirés, « h3z2y7 » devient « H3Z 2Y7 », « 123456789 » devient
 * « 12345-6789 ». Vide pour un pays sans code postal (une valeur tapée avant
 * de changer de pays ne doit pas partir).
 */
export function normalizePostalCode(country, value) {
    if (!hasPostalCode(country)) return "";
    const clean = String(value || "")
        .trim()
        .replace(/\s+/g, " ")
        .toUpperCase();
    if (!clean) return "";

    if (country === "US") {
        const digits = clean.replace(/[\s-]/g, "");
        if (/^\d{5}$/.test(digits)) return digits;
        if (/^\d{9}$/.test(digits)) {
            return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        }
    }
    if (country === "CA") {
        const compact = clean.replace(/[\s-]/g, "");
        if (compact.length === 6) {
            return `${compact.slice(0, 3)} ${compact.slice(3)}`;
        }
    }
    return clean;
}

/**
 * Clé de traduction de l'erreur du code postal, ou null s'il convient.
 * Seuls les États-Unis et le Canada ont un format vérifié : ailleurs on ne
 * refuse jamais un code, faute de pouvoir le juger sans se tromper.
 *
 * @param {string} country code ISO du pays
 * @param {string} postalCode code déjà passé par normalizePostalCode
 */
export function getPostalCodeError(country, postalCode) {
    if (!hasPostalCode(country)) return null;

    if (!postalCode) {
        if (!isPostalCodeRequired(country)) return null;
        if (country === "US") return "address_zipCode_required_us";
        if (country === "CA") return "address_zipCode_required_ca";
        return "address_zipCode_required";
    }

    if (country === "US" && !US_ZIP_CODE.test(postalCode)) {
        return "address_zipCode_invalid_us";
    }
    if (country === "CA" && !CA_POSTAL_CODE.test(postalCode)) {
        return "address_zipCode_invalid_ca";
    }
    return null;
}
